import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  API_ROUTES,
  BJ,
  RL,
  betLabel,
  clampRlBet,
  colorOf,
  isBetWinner,
  payoutAmount,
  replayableBets,
  type BjLeaderboardRow,
  type BjWallet,
  type ReplayBet,
  type RlPlaceKind,
  type RlPublicBet,
} from "@ziolo/shared";
import { Button, Card } from "@ziolo/ui";
import { apiRequest, errorMessage } from "../lib/apiClient.js";
import { useSession } from "../session/SessionProvider.js";
import { ShopPanel } from "../games/shop/ShopPanel.js";
import { RuletkaBoard } from "../games/ruletka/RuletkaBoard.js";
import { RuletkaHelp } from "../games/ruletka/RuletkaHelp.js";
import { RuletkaWheel } from "../games/ruletka/RuletkaWheel.js";
import { useRuletkaSocket } from "../games/ruletka/useRuletkaSocket.js";
import { CasinoOnPageAdmin } from "../components/admin/CasinoAdminTools.js";
import "../styles/ruletka.css";
import "../styles/shop.css";
import "../styles/blackjack.css";
import "../styles/skins.css";

function formatHms(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function useCountdown(deadline: number | null): number {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (deadline === null) {
      setLeft(0);
      return;
    }
    const tick = () => setLeft(Math.max(0, Math.ceil((deadline - Date.now()) / 1000)));
    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [deadline]);
  return left;
}

function useLeaderboard(refreshToken: number) {
  const [rows, setRows] = useState<BjLeaderboardRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      apiRequest<BjLeaderboardRow[]>(API_ROUTES.blackjack.leaderboard)
        .then((next) => {
          if (!cancelled) {
            setRows(next);
            setError(null);
          }
        })
        .catch((cause: unknown) => {
          if (!cancelled) setError(errorMessage(cause));
        });
    };
    load();
    const id = window.setInterval(load, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [refreshToken]);

  return { rows, error };
}

function useWallet(enabled: boolean, refreshToken: number) {
  const [wallet, setWallet] = useState<BjWallet | null>(null);

  useEffect(() => {
    if (!enabled) {
      setWallet(null);
      return;
    }
    let cancelled = false;
    apiRequest<BjWallet>(API_ROUTES.blackjack.wallet)
      .then((next) => {
        if (!cancelled) setWallet(next);
      })
      .catch(() => {
        // Socket session will fill this in if the HTTP call races a logout.
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, refreshToken]);

  return wallet;
}

function phaseCopy(phase: string): string {
  if (phase === "betting") return "Czas na zakłady";
  if (phase === "spinning") return "Koło się kręci";
  return "Wynik";
}

function hubLabel(phase: string, winningNumber: number | null): string {
  if (phase === "betting") return "Stawiaj";
  if (phase === "spinning") return "Koniec zakładów";
  if (winningNumber === null) return "—";
  return String(winningNumber);
}

export function RuletkaPage() {
  const { user, restoring } = useSession();
  const enabled = Boolean(user) && !restoring;
  const {
    table,
    session,
    chat,
    notice,
    connected,
    error,
    walletEpoch,
    spin,
    join,
    leave,
    placeBet,
    clearBets,
    sendChat,
    claimBonus,
  } = useRuletkaSocket(enabled);
  const [adminTick, setAdminTick] = useState(0);
  const board = useLeaderboard(walletEpoch + adminTick);
  const wallet = useWallet(enabled, walletEpoch);
  const [chip, setChip] = useState<number>(RL.DEFAULT_BET);
  const [chipDraft, setChipDraft] = useState(String(RL.DEFAULT_BET));
  const [chatDraft, setChatDraft] = useState("");
  const [chatOpen, setChatOpen] = useState(false);
  const [rankOpen, setRankOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [trackMode, setTrackMode] = useState<"straight" | "neighbors">("straight");
  const [lastRound, setLastRound] = useState<ReplayBet[]>([]);
  const chatEnd = useRef<HTMLDivElement | null>(null);

  const bettingLeft = useCountdown(table.phase === "betting" ? table.bettingEndsAt : null);
  const spinLeft = useCountdown(table.phase === "spinning" ? table.spinEndsAt : null);
  const resultLeft = useCountdown(table.phase === "results" ? table.resultEndsAt : null);
  const bonusLeft = useCountdown(session?.canClaim ? null : (session?.nextAvailableAt ?? wallet?.nextBonusAt ?? null));

  const userId = user?.id ?? 0;
  const me = table.players.find((player) => player.userId === userId);
  const seated = Boolean(me) || Boolean(session?.seated);
  const chips = me?.chips ?? session?.chips ?? wallet?.chips ?? 0;
  const keys = me?.keys ?? session?.keys ?? wallet?.keys ?? 0;
  const displayedWin = table.phase === "betting" ? null : (spin?.winningNumber ?? table.winningNumber);
  const myBets = table.bets.filter((bet) => bet.userId === userId);
  const myWin = table.winners.filter((winner) => winner.userId === userId);
  const myPayout = myWin.reduce((sum, winner) => sum + winner.winnings, 0);

  useEffect(() => {
    chatEnd.current?.scrollIntoView({ block: "end" });
  }, [chat.length]);

  useEffect(() => {
    if (!shopOpen && !helpOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShopOpen(false);
        setHelpOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [shopOpen, helpOpen]);

  useEffect(() => {
    if (table.phase !== "spinning") return;
    const mine = table.bets.filter((bet) => bet.userId === userId);
    if (mine.length > 0) setLastRound(replayableBets(mine));
  }, [table.phase, table.roundNumber, table.bets, userId]);

  if (restoring) {
    return <p className="hub-hint">Ładowanie…</p>;
  }

  if (!user) {
    return (
      <Card title="Ruletka" icon="🎯">
        <p className="hub-hint">Zaloguj się w belce na górze, żeby usiąść przy stole.</p>
      </Card>
    );
  }

  function submitChat(event: FormEvent) {
    event.preventDefault();
    const text = chatDraft.trim();
    if (!text) return;
    sendChat(text);
    setChatDraft("");
  }

  function setAmount(next: number) {
    const clamped = clampRlBet(next);
    setChip(clamped);
    setChipDraft(String(clamped));
  }

  function onPlace(kind: RlPlaceKind, numbers: number[] = []) {
    if (!seated || table.phase !== "betting") return;
    placeBet(kind, chip, numbers);
  }

  function repeatLast() {
    if (!seated || table.phase !== "betting") return;
    for (const bet of lastRound) {
      placeBet(bet.kind, bet.amount, bet.numbers);
    }
  }

  const canClaim = (session?.canClaim ?? wallet?.canClaimBonus ?? false) && connected;
  const bonusLabel = canClaim
    ? `Bonus +${BJ.BONUS_AMOUNT}`
    : bonusLeft > 0
      ? `Bonus in ${formatHms(bonusLeft * 1000)}`
      : `Bonus +${BJ.BONUS_AMOUNT}`;
  const phaseLeft =
    table.phase === "betting" ? bettingLeft : table.phase === "spinning" ? spinLeft : resultLeft;
  const bettingOpen = seated && table.phase === "betting" && connected;

  const rankBlock = (
    <>
      <h3>Ranking</h3>
      {board.error ? <p className="hub-error">{board.error}</p> : null}
      {board.rows.length === 0 && !board.error ? (
        <p className="hub-hint">Jeszcze nikt nie usiadł.</p>
      ) : (
        <ol className="rl-rank__list">
          {board.rows.map((row) => (
            <li key={row.username}>
              <span>{row.username}</span>
              <strong>{row.maxChips}</strong>
            </li>
          ))}
        </ol>
      )}
    </>
  );

  return (
    <>
    <div className="rl-page">
      {chatOpen ? (
        <button
          type="button"
          className="rl-chat-overlay"
          aria-label="Zamknij chat"
          onClick={() => setChatOpen(false)}
        />
      ) : null}

      <aside className={`rl-chat ${chatOpen ? "rl-chat--open" : ""}`}>
        <div className="rl-chat__head">
          <h3>Chat</h3>
          <button type="button" className="rl-chat__close" onClick={() => setChatOpen(false)}>
            ✕
          </button>
        </div>
        <section className="rl-rank rl-rank--side">{rankBlock}</section>
        <ol className="rl-history">
          {table.lastWinningNumbers.length === 0 ? (
            <li className="hub-hint">Brak historii</li>
          ) : (
            table.lastWinningNumbers.map((n, index) => (
              <li key={`${n}-${index}`} className={`rl-history__n rl-history__n--${colorOf(n)}`}>
                {n}
              </li>
            ))
          )}
        </ol>
        <ul className="rl-chat__log">
          {chat.map((message, index) => {
            const mine = !message.isDealer && message.user === user.username;
            const lineClass = [
              "rl-chat__line",
              message.isDealer ? "rl-chat__line--dealer" : "",
              mine ? "rl-chat__line--mine" : "",
            ]
              .filter(Boolean)
              .join(" ");
            return (
              <li key={`${message.ts}-${index}`} className={lineClass}>
                <strong>{message.user}</strong>
                <span>{message.text}</span>
              </li>
            );
          })}
          <div ref={chatEnd} />
        </ul>
        <form className="rl-chat__form" onSubmit={submitChat}>
          <input
            value={chatDraft}
            onChange={(event) => setChatDraft(event.target.value)}
            maxLength={RL.MAX_CHAT_LENGTH}
            placeholder="Napisz…"
            autoComplete="off"
          />
          <Button type="submit" size="sm" variant="primary">
            Wyślij
          </Button>
        </form>
      </aside>

      <div className="rl-main">
        <div className="rl-toolbar">
          <div className="rl-hud">
            <div className="rl-hud__stat">
              Żetony: <strong>{chips}</strong>
            </div>
            <div className="rl-hud__stat">
              Klucze: <strong>{keys}</strong>
            </div>
            <Button size="sm" variant="primary" className="rl-bonus" disabled={!canClaim} onClick={claimBonus}>
              {bonusLabel}
            </Button>
            <Button size="sm" onClick={() => setShopOpen(true)}>
              Sklep
            </Button>
            <Button size="sm" onClick={() => setHelpOpen(true)}>
              Pomoc
            </Button>
            {seated ? (
              <Button size="sm" onClick={leave}>
                Opuść stół
              </Button>
            ) : (
              <Button size="sm" variant="primary" disabled={!connected} onClick={join}>
                Dołącz
              </Button>
            )}
            <button
              type="button"
              className={`rl-rank-toggle ${rankOpen ? "rl-rank-toggle--on" : ""}`}
              onClick={() => setRankOpen((open) => !open)}
              aria-expanded={rankOpen}
            >
              🏆
            </button>
            <button type="button" className="rl-chat-toggle" onClick={() => setChatOpen(true)}>
              💬
            </button>
          </div>
        </div>

        <div className="rl-status">
          {notice ? <p className="rl-notice">{notice}</p> : null}
          {error ? <p className="hub-error">{error}</p> : null}
          {!connected && enabled ? <p className="hub-hint">Łączenie ze stołem…</p> : null}
        </div>

        <div className="rl-felt">
          <div className="rl-top">
            <RuletkaWheel
              winningNumber={displayedWin}
              spinning={table.phase === "spinning"}
              spinMs={RL.SPIN_MS}
              roundNumber={spin?.roundNumber ?? table.roundNumber}
              hubLabel={hubLabel(table.phase, displayedWin)}
            />
            <div className="rl-clock" data-phase={table.phase}>
              <span className="rl-clock__n">{phaseLeft > 0 ? phaseLeft : "—"}</span>
              <span className="rl-clock__label">{phaseCopy(table.phase)}</span>
            </div>
            <div className="rl-side">
              <section className="rl-panel">
                <h3>Przy stole</h3>
                {table.players.length === 0 ? (
                  <p className="hub-hint">Nikogo nie ma</p>
                ) : (
                  <ul className="rl-players">
                    {table.players.map((player) => (
                      <li key={player.userId}>
                        <span className="rl-swatch" style={{ background: player.color }} />
                        <span>{player.name}</span>
                        <strong>{player.chips}</strong>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
              <section className="rl-panel">
                <h3>Zakłady</h3>
                {table.bets.length === 0 ? (
                  <p className="hub-hint">Brak zakładów</p>
                ) : (
                  <ul className="rl-bets">
                    {summarizeBets(table.bets).map((row) => (
                      <li key={row.key}>
                        <span>{row.name}</span>
                        <span>{row.label}</span>
                        <strong>{row.amount}</strong>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
              {table.phase === "results" ? (
                <ResultsPanel
                  winningNumber={displayedWin}
                  myBets={myBets}
                  myPayout={myPayout}
                  seated={seated}
                />
              ) : null}
            </div>
          </div>

          <div className="rl-controls">
            <div className="rl-chips">
              {RL.CHIP_DENOMS.map((amount) => (
                <button
                  key={amount}
                  type="button"
                  className={`rl-chip-btn${chip === amount ? " rl-chip-btn--on" : ""}`}
                  onClick={() => setAmount(amount)}
                >
                  {amount}
                </button>
              ))}
              <label className="rl-amount">
                <span>Kwota</span>
                <input
                  type="number"
                  min={RL.MIN_BET}
                  max={RL.MAX_BET}
                  value={chipDraft}
                  onChange={(event) => setChipDraft(event.target.value)}
                  onBlur={() => setAmount(Number(chipDraft))}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") setAmount(Number(chipDraft));
                  }}
                />
              </label>
              <button
                type="button"
                className={`rl-mode${trackMode === "neighbors" ? " rl-mode--on" : ""}`}
                onClick={() => setTrackMode((mode) => (mode === "neighbors" ? "straight" : "neighbors"))}
              >
                Sąsiedzi ±2
              </button>
              <Button size="sm" disabled={!bettingOpen || lastRound.length === 0} onClick={repeatLast}>
                Powtórz
              </Button>
              <Button size="sm" disabled={!bettingOpen || myBets.length === 0} onClick={clearBets}>
                Wyczyść
              </Button>
            </div>
            {!seated ? <p className="hub-hint">Dołącz, żeby stawiać. Klik na polu kładzie wybraną kwotę.</p> : null}
          </div>

          <RuletkaBoard
            bets={table.bets}
            winningNumber={displayedWin}
            disabled={!bettingOpen}
            trackMode={trackMode}
            onPlace={onPlace}
          />
        </div>

        <div className={`rl-rank rl-rank--overlay ${rankOpen ? "rl-rank--open" : ""}`}>{rankBlock}</div>
      </div>

      {helpOpen ? <RuletkaHelp onClose={() => setHelpOpen(false)} /> : null}

      {shopOpen ? (
        <div
          className="shop-modal-root"
          onClick={(event) => {
            if (event.target === event.currentTarget) setShopOpen(false);
          }}
        >
          <ShopPanel variant="modal" onClose={() => setShopOpen(false)} initialChips={chips} initialKeys={keys} />
        </div>
      ) : null}
    </div>
    <CasinoOnPageAdmin onChanged={() => setAdminTick((n) => n + 1)} />
    </>
  );
}

function ResultsPanel({
  winningNumber,
  myBets,
  myPayout,
  seated,
}: {
  winningNumber: number | null;
  myBets: readonly RlPublicBet[];
  myPayout: number;
  seated: boolean;
}) {
  const color = winningNumber === null ? "none" : colorOf(winningNumber);
  return (
    <section className="rl-panel rl-panel--result">
      <h3>
        Wypadło{" "}
        <span className={`rl-hit rl-hit--${color}`}>{winningNumber ?? "—"}</span>
      </h3>
      {!seated ? null : myBets.length === 0 ? (
        <p>Nie grałeś w tej rundzie.</p>
      ) : (
        <>
          <ul className="rl-results">
            {summarizeBets(myBets).map((row) => {
              const related = myBets.filter((bet) => {
                const kind = bet.parentCallBet ?? bet.kind;
                const key = `${bet.userId}:${kind}:${bet.kind === "STRAIGHT_UP" && !bet.parentCallBet ? bet.numbers[0] : bet.numbers.join(",")}`;
                return key === row.key;
              });
              const won = related.some(
                (bet) => winningNumber !== null && isBetWinner(bet.kind, bet.numbers, winningNumber),
              );
              const pay = related.reduce(
                (sum, bet) =>
                  winningNumber !== null && isBetWinner(bet.kind, bet.numbers, winningNumber)
                    ? sum + payoutAmount(bet.amount, bet.payout, true)
                    : sum,
                0,
              );
              return (
                <li key={row.key} className={won ? "rl-results__win" : "rl-results__lose"}>
                  <span>{row.label}</span>
                  <strong>{won ? `+${pay}` : `−${row.amount}`}</strong>
                </li>
              );
            })}
          </ul>
          <p className="rl-results__total">
            {myPayout > 0 ? `Wypłata: ${myPayout}` : "Tym razem pudło."}
          </p>
        </>
      )}
    </section>
  );
}

function summarizeBets(bets: readonly RlPublicBet[]): Array<{ key: string; name: string; label: string; amount: number }> {
  const rows = new Map<string, { key: string; name: string; label: string; amount: number }>();
  for (const bet of bets) {
    const kind = bet.parentCallBet ?? bet.kind;
    const key = `${bet.userId}:${kind}:${bet.kind === "STRAIGHT_UP" && !bet.parentCallBet ? bet.numbers[0] : bet.numbers.join(",")}`;
    const current = rows.get(key);
    const label = bet.parentCallBet ? betLabel(bet.parentCallBet) : betLabel(bet.kind, bet.numbers);
    rows.set(key, {
      key,
      name: bet.playerName,
      label,
      amount: (current?.amount ?? 0) + bet.amount,
    });
  }
  return [...rows.values()];
}
